
import React, { useState, useEffect } from 'react';
import AccessibilityIcon from './AccessibilityIcon';
import { Button } from '@/components/ui/button';
import AccessibilityPanel from './AccessibilityPanel';
import VoiceAssistant from './VoiceAssistant';
import { VoiceConfig } from '@/utils/voiceUtils';
import { useLocation } from 'react-router-dom';
import { useTheme } from './theme-provider';

const SITE_ROUTES: Record<string, string> = {
  'home': '/',
  'installation': '/installation',
  'demo': '/demo',
  'pricing': '/pricing',
  'account': '/account',
  'subscribe': '/subscribe',
};

const SETTINGS_KEY = 'accesstopia_accessibility_settings';

const getSavedSettings = () => {
  try {
    const saved = localStorage.getItem(SETTINGS_KEY);
    if (!saved) return null;
    return JSON.parse(saved);
  } catch {
    return null;
  }
};

const EN_RESPONSES = {
  help: "I can help you navigate and adjust settings. For example, try 'go to home', 'toggle panel', 'turn on dark mode', 'increase font size', or 'enable large pointer'.",
  notFound: "I'm sorry, I didn't understand that command. Try asking for help to see what I can do.",
  navigate: "Navigating to",
  searchPrefix: "Searching for"
};

const voiceCustomPlugins = [
  {
    triggers: ["toggle high contrast", "enable high contrast"],
    alias: ["turn on contrast"],
    action: (command: string) => {
      const element = document.querySelector('[data-testid="accessibility-toggle"]');
      if (element) (element as HTMLElement).click();
    },
    description: "Toggled high contrast mode"
  },
  {
    triggers: "say hello",
    action: () => alert("Hello from the custom command system!"),
    alias: ["greet", "hello plugin"],
    description: "Says hello"
  },
  {
    triggers: "special demo",
    action: () => alert("This only works on the demo page!"),
    condition: (_cmd: string, ctx: any) => ctx?.path === "/demo",
    description: "Demo page special"
  }
];

const voiceConfig: VoiceConfig = {
  routes: SITE_ROUTES,
  language: 'en-US',
  commands: {
    'toggle panel': () => {
      const element = document.querySelector('[data-testid="accessibility-toggle"]');
      if (element) {
        (element as HTMLElement).click();
      }
    }
  },
  customCommandPlugins: voiceCustomPlugins,
  customResponses: EN_RESPONSES,
  lang: 'en', // ready for future i18n
};

const AccessibilityWidget = () => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  // Remove isVoiceActive, isVoiceVisible controlled floating toggle.
  const [isVoiceVisible, setIsVoiceVisible] = useState<boolean>(() => {
    const saved = getSavedSettings();
    return saved ? (saved.isVoiceAssistantVisible ?? true) : true;
  });

  const location = useLocation();
  const { theme, setTheme } = useTheme();

  // Fix: force theme to system/light at start (out of sync bug fix)
  useEffect(() => {
    const saved = getSavedSettings();
    if (saved && typeof saved.isDarkMode === "boolean") {
      setTheme(saved.isDarkMode ? "dark" : "light");
    } else {
      setTheme("light");
    }
  }, []);

  const togglePanel = () => setIsPanelOpen((prev) => !prev);

  const handleVoiceVisibilityToggle = (isVisible: boolean) => {
    setIsVoiceVisible(isVisible);
    const saved = getSavedSettings() || {};
    saved.isVoiceAssistantVisible = isVisible;
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(saved));
  };

  useEffect(() => {
    const handleStorage = () => {
      const saved = getSavedSettings();
      setIsVoiceVisible(saved ? (saved.isVoiceAssistantVisible ?? true) : true);
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return (
    <>
      {/* Main accessibility toggle button, always bottom left */}
      <Button
        variant="default"
        size="icon"
        onClick={togglePanel}
        aria-label={isPanelOpen ? "Close accessibility panel" : "Open accessibility panel"}
        aria-expanded={isPanelOpen}
        aria-controls="accessibility-panel"
        aria-haspopup="dialog"
        className="fixed left-4 bottom-4 z-50 rounded-full h-14 w-14 shadow-lg btn-accessibility focus-visible:ring-offset-4"
        title={isPanelOpen ? "Close accessibility panel" : "Open accessibility panel"}
        role="button"
        data-testid="accessibility-toggle"
        style={{
          backgroundColor: theme === 'dark' ? 'hsl(var(--secondary))' : 'white',
          transition: 'none'
        }}
      >
        <AccessibilityIcon className="h-8 w-8 text-primary dark:text-white" aria-hidden="true" />
        <span className="sr-only">Accessibility Options</span>
      </Button>

      {/* Accessibility panel */}
      <AccessibilityPanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        onVoiceAssistantToggle={handleVoiceVisibilityToggle}
      />

      {/* No separate blue voice assistant button on the right */}
      {/* VoiceAssistant is only rendered (as floating bottom-center) if visible */}
      {isVoiceVisible && (
        <VoiceAssistant
          isActive={true}
          onToggle={() => {}} // No separate toggle; activation handled via VoiceAssistant panel/settings.
          config={{
            ...voiceConfig,
            language: voiceConfig.language,
            currentPath: location.pathname
          }}
        />
      )}

      {isPanelOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
          onClick={() => setIsPanelOpen(false)}
          aria-hidden="true"
          role="presentation"
        />
      )}
    </>
  );
};

export default AccessibilityWidget;
